// Minnesotaänger Mini App — middle column.
// Restored to telegram-tt's MiddleColumn contract so the baseline
// (0df85b8d classic) MiddleColumn.scss applies: `.messages-layout` (flex
// column) with the in-flow MiddleHeader, the MessageList as its flex:1
// child and the in-flow `.middle-column-footer` holding the
// FloatingActionButtons (scroll-down) and the Composer. The footer's
// height is synced into `--middle-footer-content-height` (ResizeObserver +
// keyboard-viewport listener, both from telegram-tt). Removed: slide
// Transitions, pinned-message pane, footer action bars and modals.

import { memo, useEffect, useRef, useState } from '@teact';

import type { ElementRef } from '@teact';

import { getActions, withGlobal } from '../../global';
import { selectActiveChatId } from '../../global/selectors';
import {
  selectComposerText,
  selectIsSending,
  selectPendingOperations,
  selectReplyingToEventId,
  selectSelectedMessageIds,
  selectSendError,
} from '../../global/selectors/miniapp';
import type { MiniAppPendingOperation } from '../../global/miniapp/types';

import { IS_ANDROID, IS_IOS } from '../../util/browser/windowEnvironment';
import { requestMeasure, requestMutation } from '../../lib/fasterdom/fasterdom';
import captureEscKeyListener from '../../util/captureEscKeyListener';
import useLastCallback from '../../hooks/useLastCallback';

import Button from '../ui/Button';
import Composer from './composer/Composer';
import FloatingActionButtons from './FloatingActionButtons';
import MessageList from './MessageList';
import MiddleHeader from './MiddleHeader';

import { measureFooterContentHeight, syncMessageListBottomReserve } from './helpers/messageListReserves';

import './MiddleColumn.scss';

type OwnProps = {
  leftColumnRef?: ElementRef<HTMLDivElement>;
  isMobile?: boolean;
};

type StateProps = {
  chatId?: string;
  // Pending ops (§3.4) and the send-failure banner (§3.3) and selection bar
  // (§3.5) project shared state into the column between list and composer.
  pendingOperations: MiniAppPendingOperation[];
  sendFailed: string | null;
  selectedMessageIds: string[];
  composerText: string;
  // v1 (fix-list E): reply target is an event_id (l0 rowid).
  replyingToEventId?: number;
  isSending: boolean;
};

const KEYBOARD_SETTLE_DURATION = 400;

// One banner line per pending op kind (CHAT_CONTRACT §3.4).
function getPendingOpLabel(kind: MiniAppPendingOperation['kind']): string {
  switch (kind) {
    case 'send':
      return '正在等待助手回复…';
    case 'reroll':
      return '正在重新生成…';
    case 'edit':
      return '正在编辑…';
    case 'delete':
      return '正在删除…';
  }
}

function MiddleColumn({
  chatId,
  pendingOperations,
  sendFailed,
  selectedMessageIds,
  composerText,
  replyingToEventId,
  isSending,
  isMobile,
}: OwnProps & StateProps) {
  const { openPreviousChat, copySelectedMessages, exitMessageSelectMode, sendMessage } = getActions();
  const [isScrollDownNeeded, setIsScrollDownNeeded] = useState(false);
  const middleColumnRef = useRef<HTMLDivElement>();
  const scrollerRef = useRef<HTMLDivElement>();
  const isViewportAnimatingRef = useRef(false);
  const viewportSettleTimerRef = useRef<number>();

  useEffect(() => (
    chatId ? captureEscKeyListener(() => {
      openPreviousChat();
    }) : undefined
  ), [chatId, openPreviousChat]);

  const handleScrollDown = useLastCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
  });

  // Retry re-sends the last attempt with the same text/reply. The text is
  // still in shared state (§3.3) — it is only cleared when a send is accepted.
  const handleRetrySend = useLastCallback(() => {
    if (!chatId || isSending) return;
    // v1 (fix-list E): reply target is an event_id.
    sendMessage({ chatId, text: composerText, replyToEventId: replyingToEventId });
  });

  const syncFooterSlide = useLastCallback((footer: HTMLElement) => {
    if (!footer.offsetParent) return;

    const contentHeight = measureFooterContentHeight(footer);
    requestMutation(() => {
      footer.style.setProperty('--middle-footer-content-height', `${contentHeight}px`);
    });

    const scroller = footer.parentElement?.querySelector<HTMLElement>('.MessageList');
    if (scroller) syncMessageListBottomReserve(scroller, isViewportAnimatingRef.current);
  });

  const updateFooterHeight = useLastCallback(() => {
    const middleColumn = middleColumnRef.current;
    if (!middleColumn) return;

    middleColumn.querySelectorAll<HTMLElement>('.middle-column-footer').forEach((footer) => {
      syncFooterSlide(footer);
    });
  });

  // Keep the list's bottom reserve in sync with the absolute composer footer.
  useEffect(() => {
    const middleColumn = middleColumnRef.current;
    if (!middleColumn) return undefined;

    updateFooterHeight();

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => syncFooterSlide(entry.target as HTMLElement));
    });
    middleColumn.querySelectorAll<HTMLElement>('.middle-column-footer').forEach((footer) => {
      observer.observe(footer, { box: 'border-box' });
    });

    return () => observer.disconnect();
  }, [chatId, updateFooterHeight, syncFooterSlide]);

  // Fix for mobile virtual keyboard (from telegram-tt): when the keyboard
  // opens, `body.keyboard-visible` lifts the footer above it; after the
  // viewport settles, the footer height and list reserve are re-synced and
  // the window scroll offset (if any) is reset.
  useEffect(() => {
    if (!IS_IOS && !IS_ANDROID) {
      return undefined;
    }

    const { visualViewport } = window;
    if (!visualViewport) {
      return undefined;
    }

    const markViewportSettled = () => {
      window.clearTimeout(viewportSettleTimerRef.current);
      viewportSettleTimerRef.current = window.setTimeout(() => {
        isViewportAnimatingRef.current = false;
        updateFooterHeight();
      }, KEYBOARD_SETTLE_DURATION);
    };

    const handleResize = () => {
      isViewportAnimatingRef.current = true;
      markViewportSettled();

      const isFixNeeded = visualViewport.height !== document.documentElement.clientHeight;

      requestMutation(() => {
        document.body.classList.toggle('keyboard-visible', isFixNeeded);

        requestMeasure(() => {
          if (!isFixNeeded && visualViewport.offsetTop) {
            requestMutation(() => {
              window.scrollTo({ top: 0 });
            });
          }
        });
      });
    };

    visualViewport.addEventListener('resize', handleResize);

    return () => {
      visualViewport.removeEventListener('resize', handleResize);
      window.clearTimeout(viewportSettleTimerRef.current);
    };
  }, [updateFooterHeight]);

  return (
    <div id="MiddleColumn" ref={middleColumnRef} className="ui-ready">
      {chatId ? (
        <div className="messages-layout">
          {/* v1 (fix-list D): wire isMobile so MiddleHeader can use the iOS
              back-gesture / blur behaviors it already implements. */}
          <MiddleHeader chatId={chatId} isMobile={isMobile} />
          <MessageList chatId={chatId} scrollerRef={scrollerRef} onScrollDownToggle={setIsScrollDownNeeded} />
          {pendingOperations.length > 0 && (
            <div className="pending-op-banner">
              {pendingOperations.map((op) => (
                <span key={`${op.kind}-${op.targetEventId ?? op.startedAt}`}>{getPendingOpLabel(op.kind)}</span>
              ))}
            </div>
          )}
          {sendFailed && (
            <div className="send-failed-banner">
              <span>{sendFailed}</span>
              <Button size="tiny" onClick={handleRetrySend}>重试</Button>
            </div>
          )}
          {selectedMessageIds.length > 0 && (
            <div className="message-selection-bar">
              <span className="message-selection-count">已选择 {selectedMessageIds.length} 条</span>
              <Button size="tiny" onClick={() => copySelectedMessages()}>复制</Button>
              <Button size="tiny" color="secondary" onClick={() => exitMessageSelectMode()}>取消</Button>
            </div>
          )}
          <div className="middle-column-footer">
            <FloatingActionButtons withScrollDown={isScrollDownNeeded} onClick={handleScrollDown} />
            <Composer chatId={chatId} />
          </div>
        </div>
      ) : undefined}
    </div>
  );
}

export default memo(withGlobal<OwnProps>((global): StateProps => {
  const chatId = selectActiveChatId(global);
  return {
    chatId,
    pendingOperations: chatId ? selectPendingOperations(global, chatId) : [],
    sendFailed: chatId ? selectSendError(global, chatId) : null,
    selectedMessageIds: chatId ? selectSelectedMessageIds(global, chatId) : [],
    composerText: chatId ? selectComposerText(global, chatId) : '',
    replyingToEventId: chatId ? selectReplyingToEventId(global, chatId) : undefined,
    isSending: chatId ? selectIsSending(global, chatId) : false,
  };
})(MiddleColumn));
